'use client'

import { useEffect, useRef } from 'react'
import { useSession } from 'next-auth/react'

// Maps the backend hierarchy level slug to the expected Keycloak realm role.
// Must stay in sync with keycloak_role_sync.py SLUG_TO_KEYCLOAK_ROLE.
const SLUG_TO_ROLE: Record<string, string> = {
  superadmin:      'super_admin',
  super_admin:     'super_admin',
  quality_admin:   'quality_admin',
  quality_manager: 'quality_manager',
  hod:             'admin',
  process_head:    'supervisor',
  manager:         'supervisor',
  supervisor:      'supervisor',
  telecaller:      'telecaller',
}

/**
 * Runs once per authenticated session.
 * Calls /api/auth/team-context (which also triggers the backend role sync),
 * then compares the employee's hierarchy level against the JWT roles.
 * If they don't match — meaning the role was just assigned by the backend
 * but the old JWT still has "user" — forces a silent token refresh so the
 * new role appears immediately without requiring a manual re-login.
 */
export function RoleSyncGuard() {
  const { data: session, status, update } = useSession()
  const hasSynced = useRef(false)

  useEffect(() => {
    if (status !== 'authenticated' || hasSynced.current) return
    if (!session?.accessToken) return

    hasSynced.current = true

    const run = async () => {
      try {
        const basePath = process.env.NEXT_PUBLIC_BASE_PATH || ''
        const apiBase = process.env.NEXT_PUBLIC_API_URL || ''

        const res = await fetch(`${apiBase}${basePath}/api/auth/team-context`, {
          headers: { Authorization: `Bearer ${session.accessToken}` },
        })
        if (!res.ok) return

        const data = await res.json()
        const levelSlug: string | null = data?.level_slug ?? null
        if (!levelSlug) return

        const expectedRole = SLUG_TO_ROLE[levelSlug]
        if (!expectedRole) return

        const currentRoles: string[] = session?.roles ?? []

        // If the JWT doesn't yet contain the correct role, refresh the token.
        if (!currentRoles.includes(expectedRole)) {
          console.log(
            `[RoleSyncGuard] Role mismatch: JWT has [${currentRoles.join(', ')}], ` +
            `DB says level="${levelSlug}" → expecting "${expectedRole}". Refreshing token…`
          )
          await update()
        }
      } catch {
        // Non-critical — ignore silently
      }
    }

    run()
  }, [status, session?.accessToken, session?.roles, update])

  return null
}
